import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchRun, listRuns, triggerRun, type Run } from '../../api/runs';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { DataState } from '../../components/DataState';
import { StatusChip } from '../../components/StatusChip';
import { formatDateTime, formatDuration } from '../../components/formatters';

export function RunsHealthPage() {
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();

  const runsQuery = useQuery({
    queryKey: ['runs', 'list', 50],
    queryFn: ({ signal }) => listRuns(50, signal).then((r) => r.runs),
    // Poll while a manual run is in flight so new run rows appear without a reload.
    refetchInterval: autoRefresh ? 5000 : false,
  });

  const trigger = useMutation({
    mutationFn: triggerRun,
    onSuccess: (_data, type) => {
      setFeedback({
        severity: 'success',
        message:
          type === 'latest'
            ? 'Latest-data run started — a new run will appear below shortly.'
            : 'Historical backfill started — runs appear gradually, one per month. This can take a while.',
      });
      setAutoRefresh(true);
      queryClient.invalidateQueries({ queryKey: ['runs'] });
    },
    onError: (err: unknown) => {
      setFeedback({
        severity: 'error',
        message: err instanceof ApiError ? err.message : 'Failed to start run.',
      });
    },
  });

  return (
    <Box>
      <PageHeader
        title="Runs & Health"
        subtitle="Recent pipeline runs, errors, and per-source freshness"
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<CloudDownloadRoundedIcon />}
              disabled={trigger.isPending}
              onClick={() => trigger.mutate('latest')}
            >
              Get latest data
            </Button>
            <Button
              variant="contained"
              startIcon={<HistoryRoundedIcon />}
              disabled={trigger.isPending}
              onClick={() => trigger.mutate('historical')}
            >
              {`Get historical data (Jan 2025 – Dec ${currentYear})`}
            </Button>
          </>
        }
      />

      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      <DataState
        loading={runsQuery.isLoading}
        error={runsQuery.error}
        data={runsQuery.data}
        isEmpty={(r) => r.length === 0}
        emptyMessage="No runs recorded yet."
      >
        {(runs) => (
          <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Triggered by</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Sources (ok / fail)</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell align="right">Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map((r) => (
                    <RunRow key={r.runId} run={r} onOpen={() => setOpenRunId(r.runId)} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </DataState>

      <RunDetailDialog runId={openRunId} onClose={() => setOpenRunId(null)} />
    </Box>
  );
}

function RunRow({ run, onOpen }: { run: Run; onOpen: () => void }) {
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {formatDateTime(run.createdAt)}
        </Typography>
      </TableCell>
      <TableCell>
        <StatusChip status={run.status} />
      </TableCell>
      <TableCell>
        <Chip size="small" label={run.triggeredBy} variant="outlined" />
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {run.period.from} → {run.period.to}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {run.succeeded.length > 0 && (
            <Chip size="small" label={`${run.succeeded.length} ok`} sx={{ bgcolor: '#ECFDF5', color: '#065F46' }} />
          )}
          {run.failed.length > 0 && (
            <Chip size="small" label={`${run.failed.length} fail`} sx={{ bgcolor: '#FEF2F2', color: '#991B1B' }} />
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {formatDuration(run.durationMs)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Button size="small" endIcon={<OpenInNewRoundedIcon fontSize="small" />} onClick={onOpen}>
          View
        </Button>
      </TableCell>
    </TableRow>
  );
}

function RunDetailDialog({ runId, onClose }: { runId: string | null; onClose: () => void }) {
  const detailQuery = useQuery({
    queryKey: ['runs', 'detail', runId],
    queryFn: ({ signal }) => (runId ? fetchRun(runId, signal) : Promise.reject('no_id')),
    enabled: !!runId,
  });

  return (
    <Dialog open={!!runId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Run details
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <DataState
          loading={detailQuery.isLoading}
          error={detailQuery.error}
          data={detailQuery.data}
        >
          {({ run, snapshots, errors }) => (
            <Stack spacing={3}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <StatusChip status={run.status} />
                <Typography variant="caption" color="text.secondary">
                  {run.runId}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={3} flexWrap="wrap">
                <Field label="Period" value={`${run.period.from} → ${run.period.to}`} />
                <Field label="Triggered by" value={run.triggeredBy} />
                <Field label="Duration" value={formatDuration(run.durationMs)} />
                <Field label="Created" value={formatDateTime(run.createdAt)} />
              </Stack>

              {errors.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Errors ({errors.length})
                  </Typography>
                  <Stack spacing={1}>
                    {errors.map((e, i) => (
                      <Alert key={i} severity="error" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {e.source}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SF Mono, monospace', fontSize: 12 }}>
                          {e.reason}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {e.retriesAttempted} retries
                        </Typography>
                      </Alert>
                    ))}
                  </Stack>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Snapshots ({snapshots.length})
                </Typography>
                <TableContainer component={Box} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Source</TableCell>
                        <TableCell>Period</TableCell>
                        <TableCell>Collected</TableCell>
                        <TableCell>Metrics</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {snapshots.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Chip size="small" label={s.source} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {s.period.from} → {s.period.to}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(s.collectedAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {Object.keys(s.metrics ?? {}).length
                               + Object.keys(s.sheetMetrics ?? {}).length
                               + Object.keys(s.planSheetMetrics ?? {}).length} keys
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          )}
        </DataState>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );
}
